import type React from 'react';
import { useTranslation } from 'react-i18next';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    BalancePicture,
    BreakEven,
    CashCoverage,
    CashRunway,
    NetWorkingCapital,
    NoticeCard,
    ZoneRail,
} from './components';
import { NOTICES } from './financialPositionMeta';

/**
 * Financial Position — render only.
 *
 * The empty state of the real screen rather than a page-instead-of-page notice:
 * same heading, same rail, same cards in the same order, with each figure
 * replaced by what would produce it. A reader who sets the company up and comes
 * back finds the layout they already learned, filled in — nothing moves.
 */
export const FinancialPositionPageView: React.FC = () => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('analytics.financialPosition.title')}
            className="flex w-full flex-col gap-5"
            {...getNodeTestIdProp('financial-position-section')}
        >
            {/* No heading: the tab above already says "Financial Position",
                and a second copy of the name directly under it reads as two
                screens. The subtitle stays — it says what the screen *does*,
                which the tab label cannot. The name still names the section
                for a screen reader, through `aria-label` above. */}
            <p className="text-sm text-muted">
                {t('analytics.financialPosition.subtitle')}
            </p>
            <ZoneRail />
            {/* No PromptCards here any more (BOS-170).
                They were `cash-coverage` and `cash-runway` — the same two
                metrics rendered a second time, with the same icon, title,
                question and call to action as the live cards below, which
                already show exactly this when their inputs are missing and the
                real figure when they are not. Two cards headed "Cash coverage"
                on one screen, one of which can never show a number, reads as a
                page that rendered twice. The live card is the one that can
                still become useful, so it is the one that stayed. */}
            <div className="grid gap-4 lg:grid-cols-2">
                {NOTICES.map((notice) => (
                    <NoticeCard key={notice.id} notice={notice} />
                ))}
            </div>
            {/* The four overview cards, live off `/ledger/analytics/financial-position`.
                Each shows its own empty state when its inputs are missing. */}
            <div className="grid gap-4 lg:grid-cols-2">
                <NetWorkingCapital />
                <CashCoverage />
                <BreakEven />
                <CashRunway />
            </div>
            <BalancePicture />
        </section>
    );
};
